import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import SimpleChartJSBarChart from '@components/Charts/SimpleChartJSBarChart/SimpleChartJSBarChart';
import { useDisplayUnit, useTheme, useSeriesBuilder } from '@hooks';
import { NoDataAvailable } from '@components/Display';

function DrillingEquipmentChart({ loading, equipmentConfig, parsedEquipmentTotals }) {
  const { displayUnit, getUnitTitle } = useDisplayUnit();
  const { theme } = useTheme();
  const { getSeriesData } = useSeriesBuilder();
  const {
    palette: {
      charts: { colorsSameType }
    }
  } = theme;
  const {
    allComponentsResponse,
  } = useMemo(() => {
    if (!parsedEquipmentTotals || !equipmentConfig?.equipmentDetails)
      return {
        allComponentsResponse: { unit: '', series: [] }
      };

    const allNaN = Object.values(parsedEquipmentTotals).every(v => v == null || Number.isNaN(v));
    if (allNaN)
      return {
        allComponentsResponse: { unit: '', series: [] }
      };

    let max = 0;
    let displayDataList = [];
    const { equipmentDetails } = equipmentConfig;

    equipmentDetails.forEach(({ equipmentName, chartConfig }) => {
      const tot = parsedEquipmentTotals[equipmentName];
      if (tot == null) return;
      const displayName = chartConfig.displayName === 'Unknown component' ? equipmentName : chartConfig.displayName;
      max = Math.max(max, tot);

      let { defaultArray, convertedArray } = getSeriesData([tot], displayUnit);
      defaultArray = defaultArray.map(val => ({ x: displayName, y: val }));
      convertedArray = convertedArray.map(val => ({ x: displayName, y: val }));

      displayDataList.push({
        label: displayName,
        type: 'bar',
        order: chartConfig.order,
        group: '',
        borderColor: colorsSameType[0],
        backgroundColor: colorsSameType[0],
        borderSkipped: true,
        barPercentage: 0.6,
        stack: 1,
        defaultArray,
        data: [],
        convertedArray
      });
    });

    displayDataList = displayDataList.sort((a, b) => a.order - b.order);

    const { title, converted } = getUnitTitle(max);

    return {
      allComponentsResponse: {
        unit: title,
        series: displayDataList.map(d => ({
          ...d,
          data: converted ? d.convertedArray : d.defaultArray,
          unit: title,
          converted: converted
        }))
      }
    };
  }, [displayUnit, parsedEquipmentTotals, equipmentConfig]);

  if (!loading && NoDataAvailable.hasNoData(allComponentsResponse?.series)) {
    return <NoDataAvailable height="380px" />;
  }

  return (
    <SimpleChartJSBarChart
      id="drilling-equipment-chart"
      loading={loading}
      series={allComponentsResponse?.series}
      unit={allComponentsResponse.unit}
      tooltipTitle={displayUnit}
      // horizontal bars (categories on Y axis)
      isHorizontal
      limitLabelLength={false}
      height={'380px'}
    />
  );
}
DrillingEquipmentChart.propTypes = {
  series: PropTypes.arrayOf(PropTypes.object),
  categories: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool
};

DrillingEquipmentChart.defaultProps = {
  series: [],
  categories: [],
  loading: false
};

export default DrillingEquipmentChart;
